import { AppImageDisplayInfo } from "client/Api/AppIcon/common";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { AnnouncementsLiveInformationProvider } from "client_legacy/Workspace/LiveInformation/Hub/announcementProvider";
import { EventLiveInformationProvider } from "client_legacy/Workspace/LiveInformation/Hub/eventProvider";
import { KnowledgeItemLiveInformationProvider } from "client_legacy/Workspace/LiveInformation/Hub/knowledgeItemProvider";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { LiveTileContent } from "../LiveTileContent";
import { localize } from "client_legacy/ndaw/culture";
import { CardProps } from "./common";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	HubCardSizeInformation,
	HubCardWidth,
	HubItemLiveInformationProvider,
	useHubCardSizeInformation,
	useHubItemLiveInformation
} from "./hooks";
import { never } from "client_legacy/ndaw/errors";
import { AnnouncementCard } from "./Card/AnnouncementCard";
import { getHubUrlForEvent } from "client/Features/Hub/Events/EventsPage";
import { getHubUrlForKnowledgeItem } from "client/Features/Hub/KnowledgeItems/KnowledgeItemsPage";
import { getHubUrlForAnnouncement } from "client/Features/Hub/Announcements/AnnouncementsPage";
import { navigateTo } from "client_legacy/ndaw/navigator";
import { KnowledgeItemCard } from "./Card/KnowledgeItemCard";
import { EventCard } from "./Card/EventItemCard";
import { HubItemTileFooter } from "./HubItemTileFooter";
import { DimensionToRender } from "../../RenderState/common";
import { Guid, newGuid } from "client_legacy/ndaw/guid";
import { TileRefresh } from "../TileRefresh/TileRefresh";
import { ErrorResult, SpotlightItemIsNotAvailableResult } from "./NoResults";
import {
	DisplaySpotLightLayout,
	HubItemTileResourceId,
	HubItemTileResourceKind
} from "client/Api/Workspace/workspace";
import { BusyIndicator } from "client/Components";
import { HubDataSource } from "client/Api/Hub/hub";
import { hubItemKindToHubDataSourceMap } from "client/Features/Hub/Sidebar/common";

export type SpotlightViewProps = {
	icon: AppImageDisplayInfo;
	dimension: DimensionToRender;
	layout: DisplaySpotLightLayout;
	hideHeader: boolean;
	hideFooter: boolean;
	announcementProvider: AnnouncementsLiveInformationProvider;
	knowledgeItemProvider: KnowledgeItemLiveInformationProvider;
	eventProvider: EventLiveInformationProvider;
	timezone: TimeZone;
	onAddAnalyticsEvent: () => void;
};

export const SpotlightView = (props: SpotlightViewProps): ReactElement => {
	const {
		icon,
		dimension,
		layout,
		hideHeader,
		hideFooter,
		announcementProvider,
		eventProvider,
		knowledgeItemProvider,
		timezone,
		onAddAnalyticsEvent
	} = props;

	const { itemKind, itemId } = layout;

	// NOTE: This is an artificial tabId, since the live information provider requires tabId
	// as its party identifier, but spotlight view does not have any tabs
	const [spotlightTabId] = useState<Guid>(() => newGuid());

	const openHandler = useCallback(
		(id: number): void => {
			onAddAnalyticsEvent();

			const source: HubDataSource = hubItemKindToHubDataSourceMap[itemKind];

			let url: string;

			switch (source) {
				case HubDataSource.Announcement:
					url = getHubUrlForAnnouncement({
						source,
						itemId: id
					});
					break;
				case HubDataSource.KnowledgeItem:
					url = getHubUrlForKnowledgeItem({
						source,
						itemId: id
					});
					break;
				case HubDataSource.Event:
					url = getHubUrlForEvent({
						source,
						itemId: id
					});
					break;
				default:
					never(source);
			}

			navigateTo(url);
		},
		[
			onAddAnalyticsEvent,
			itemKind
		]
	);

	const renderContent = (): ReactElement => {
		switch (itemKind) {
			case HubItemKind.Announcement:
				return (
					<LayoutRenderer
						tabId={spotlightTabId}
						dimension={dimension}
						itemId={itemId}
						provider={announcementProvider}
						interestsFactory={() => ({
							items: true as const,
							comments: true,
							reactions: true,
							lastPinnedItem: true
						})}
						cardComponent={AnnouncementCard}
						onOpen={openHandler}
						timezone={timezone}
					/>
				);
			case HubItemKind.KnowledgeItem:
				return (
					<LayoutRenderer
						tabId={spotlightTabId}
						dimension={dimension}
						itemId={itemId}
						provider={knowledgeItemProvider}
						interestsFactory={() => ({
							items: true,
							lastPinnedItem: true
						})}
						cardComponent={KnowledgeItemCard}
						onOpen={openHandler}
						timezone={timezone}
					/>
				);
			case HubItemKind.Event:
				return (
					<LayoutRenderer
						tabId={spotlightTabId}
						dimension={dimension}
						itemId={itemId}
						provider={eventProvider}
						interestsFactory={() => ({
							items: true as const,
						})}
						cardComponent={EventCard}
						onOpen={openHandler}
						timezone={timezone}
					/>
				);
			default:
				never(itemKind);
		}
	};

	return (
		<>
			{!hideHeader && (
				<LiveTileContent.Header icon={icon}>
					<LiveTileContent.Title text={localize("_Spotlight")} />
				</LiveTileContent.Header>
			)}

			<LiveTileContent.Content>
				<LiveTileContent.ContentStack>
					{renderContent()}
				</LiveTileContent.ContentStack>
			</LiveTileContent.Content>

			{!hideFooter && (
				<HubItemTileFooter
					categoryId={undefined}
					kind={itemKind}
					onAddAnalyticsEvent={onAddAnalyticsEvent}
				/>
			)}
		</>
	);
};
SpotlightView.displayName = "SpotlightView";

type LayoutRendererProps<TItem extends { id: number; }, TInterests> = {
	tabId: Guid;
	itemId: number;
	provider: HubItemLiveInformationProvider<TItem, TInterests>;
	interestsFactory: (sizeInfo: HubCardSizeInformation) => TInterests;
	dimension: DimensionToRender;
	cardComponent: {
		(props: CardProps<TItem>): ReactElement;
	};
	onOpen: (id: number) => void;
	timezone: TimeZone;
};

const LayoutRenderer = <TItem extends { id: number; }, TInterests>(props: LayoutRendererProps<TItem, TInterests>): ReactElement | null => {
	const {
		tabId,
		itemId,
		provider,
		interestsFactory,
		dimension,
		cardComponent: CardComponent,
		onOpen,
		timezone
	} = props;

	const sizeInfo: HubCardSizeInformation = useHubCardSizeInformation(dimension);

	const resourceId = useMemo(
		(): HubItemTileResourceId | undefined => {
			return {
				kind: HubItemTileResourceKind.ItemIds,
				itemIds: [itemId]
			};
		},
		[itemId]
	);

	const [loadingState, refreshHandler] = useHubItemLiveInformation(
		provider,
		tabId,
		resourceId,
		interestsFactory(sizeInfo)
	);

	const {
		hasTooManyConsecutiveErrors,
		hasGlobalFailure,
		page,
		isLoading
	} = loadingState;

	useEffect(
		(): void => {
			if (page && page.items.length > 1) {
				console.error("Spotlight view does not support more then one item.");
			}
		},
		[page]
	);

	if (hasTooManyConsecutiveErrors) {
		return <TileRefresh onRefresh={refreshHandler} />;
	}

	if (hasGlobalFailure) {
		return <ErrorResult itemKind={provider.itemKind} onRefresh={refreshHandler} />;
	}

	if (!page) {
		return null;
	}

	if (page.items.length === 0) {
		return <SpotlightItemIsNotAvailableResult />;
	}

	const isSmall: boolean = sizeInfo.width === HubCardWidth.Narrow
		|| sizeInfo.width === HubCardWidth.UltraNarrow;

	return (
		<LiveTileContent.BusyIndicatorContainer>
			{ isLoading && <BusyIndicator /> }
			<CardComponent
				item={page.items[0]}
				size={isSmall
					? "small"
					: "large"
				}
				onOpen={onOpen}
				timezone={timezone}
			/>
		</LiveTileContent.BusyIndicatorContainer>
	);
};
LayoutRenderer.displayName = "LayoutRenderer";